import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { WorkspaceEditApi, WorkspaceEditApi_FormInitData } from "src/api/generated/workspaces/api/workspaceEditApi.ts";
import { Workspace } from "src/api/generated/postgres/db/types/workspaces/tables/workspace.ts";
import { WorkspaceConnectedItemRef } from "src/api/generated/workspaces/connections/workspaceConnectedItemRef.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormDateTimeField } from "src/components/common/forms/fields/FormDateTimeField.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface WorkspaceFormProps extends FormCommonProps<number> {
	parentId: number | null;
	workspaceId: number | null;
	connectToItem?: WorkspaceConnectedItemRef;
}

interface FormValues extends Workspace {}

interface StoredDefaultValues extends Partial<Pick<FormValues, "categoryId">> {}

export const WorkspaceForm = ({
	parentId,
	workspaceId,
	connectToItem,
	onFormEdited,
	onCompleted,
}: WorkspaceFormProps) => {
	const { appUserId } = useGlobalInitData();

	const [storedDefaultValues, setStoredDefaultValues] = useStoredState<StoredDefaultValues>({
		key: "665D1D9F8945A0CB",
		defaultValue: {},
	});

	return (
		<AsyncForm<WorkspaceEditApi_FormInitData, FormValues, number>
			fetch={() => WorkspaceEditApi.getFormInitData({ workspaceId: workspaceId, parentId: parentId })}
			getDefaultValues={({ workspace, parentCategoryId }) =>
				workspaceId != null ?
					{
						...workspace,
					}
				:	{
						...storedDefaultValues,
						parentId: parentId,
						workspaceId: workspaceId,
						isLocked: false,
						tasksEnabled: true,
						documentsEnabled: true,
						connectionsEnabled: true,
						categoryId: parentCategoryId ?? storedDefaultValues.categoryId,
						responsiblePersonId: appUserId,
					}
			}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("workspaces.workspace.form"),
				data: {
					workspaceId: workspaceId ?? null,
					parentId,
					connectToItemId: connectToItem?.itemId ?? null,
					connectToItemType: connectToItem?.itemType ?? null,
				},
			}}
			render={({ data: { categoryOptions }, control }) => (
				<>
					<FormTextField
						control={control}
						name={"name"}
						label={i18n.t("name")}
						rules={requireRule()}
						autoFocus
					/>
					<FormTextField control={control} name={"description"} label={i18n.t("description")} />
					<FormSelectField
						control={control}
						name={"categoryId"}
						label={i18n.t("category")}
						options={categoryOptions}
						getOptionKey={(o) => o.id}
						getOptionLabel={(o) => o.categoryName}
						rules={requireRule()}
					/>
					<FormAsyncUserSelectField
						control={control}
						label={i18n.t("responsible_person")}
						name={"responsiblePersonId"}
						rules={requireRule()}
					/>
					<FormDateTimeField control={control} name={"pointOfTime"} label={i18n.t("point_of_time")} />
					<FormCheckbox
						control={control}
						name={"tasksEnabled"}
						label={i18n.t("tasks_enabled")}
						startNewGridRow={true}
					/>
					<FormCheckbox
						control={control}
						name={"documentsEnabled"}
						label={i18n.t("documents_enabled")}
						startNewGridRow={true}
					/>
					<FormCheckbox
						control={control}
						name={"connectionsEnabled"}
						label={i18n.t("connections_enabled")}
						startNewGridRow={true}
					/>
				</>
			)}
		/>
	);

	async function submit(values: Workspace) {
		setStoredDefaultValues({
			categoryId: values.categoryId,
		});
		if (values.id == null) {
			return await WorkspaceEditApi.insert({ workspace: values, connectToItem: connectToItem });
		} else {
			return await WorkspaceEditApi.update({ workspace: values });
		}
	}
};
